/**
 * Access-token accessor for the OIDC panels.
 *
 * The live token comes from the pluggable `tokenSource` (keycloak-js, in
 * memory). The legacy `localStorage` keys (`accessToken`/`refreshToken`) only
 * survive so `clearTokens()` can sweep stale password-grant sessions at cutover.
 *
 * `localStorage` may be unavailable in private-browsing modes or under
 * corporate policies (REQ EC09). All accesses are wrapped in try/catch and
 * return safe fallbacks (`null`) instead of throwing.
 */

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

/**
 * Returns the current `localStorage` instance, or `null` when access is
 * blocked. We probe via a getter on `window.localStorage` because some
 * browsers throw `SecurityError` only on access, not on detection.
 */
const getStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const readKey = (key: string): string | null => {
  const storage = getStorage();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const removeKey = (key: string): void => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* swallow — already best-effort */
  }
};

/**
 * Fonte de token plugável: cada app autentica via OIDC (keycloak-js) e o token
 * vive em memória no adapter — `ensureKeycloakLogin` pluga a source. Sem source
 * (janela pré-init), o fallback lê o localStorage legado (vazio pós-cutover).
 */
let tokenSource: (() => string | null) | null = null;

export const setTokenSource = (source: (() => string | null) | null): void => {
  tokenSource = source;
};

export const getAccessToken = (): string | null =>
  tokenSource ? tokenSource() : readKey(ACCESS_TOKEN_KEY);

/**
 * Clears legacy password-grant tokens from `localStorage`. Also run once at
 * each app's boot (`main.tsx`) to sweep stale operator sessions at cutover —
 * OIDC keeps its token in memory, so these keys are dead weight.
 */
export const clearTokens = (): void => {
  removeKey(ACCESS_TOKEN_KEY);
  removeKey(REFRESH_TOKEN_KEY);
};

export const isLoggedIn = (): boolean => {
  const token = getAccessToken();
  return typeof token === 'string' && token.length > 0;
};
